import { useContext, useMemo } from "react";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import type { Accessorial } from "~/shared/schemas/accessorial-schema";
import type { AddtDetail } from "~/shared/types/quote-details-types";

/**
 * Union type for all possible accessorial-like items shown in the summary.
 */
export type SelectedAccessorialItem =
	| Accessorial
	| AddtDetail
	| { id: string | number; name: string };

/**
 * Helper to filter and format pickup/delivery services.
 */
const formatService = (
	services: Accessorial[],
	type: "Pickup" | "Delivery",
): SelectedAccessorialItem[] => {
	return services
		.filter((acc) => acc.name.toLowerCase() !== "commercial")
		.map((acc) => ({
			...acc,
			name: acc.name.includes(type) ? acc.name : `${acc.name} ${type}`,
		}));
};

/**
 * Hook to retrieve and format all selected accessorials from the quote state.
 */
export const useSelectedAccessorials = (): SelectedAccessorialItem[] => {
	const { quoteState } = useContext(QuoteDetailsContext);

	return useMemo(() => {
		const isHazmat = quoteState.items.some((item) => item.isHazmat);

		const result: SelectedAccessorialItem[] = [
			...formatService(quoteState.pickupServices, "Pickup"),
			...formatService(quoteState.deliveryServices, "Delivery"),
			...quoteState.addtDetails,
		];

		if (isHazmat) {
			result.push({ id: "hazmat", name: "Hazmat" });
		}

		return result;
	}, [
		quoteState.addtDetails,
		quoteState.deliveryServices,
		quoteState.pickupServices,
		quoteState.items,
	]);
};
